/**
 * Centralized Logger for Talousakatemia
 * 
 * Levels:
 * - info: Expected events (purchase started, session refreshed)
 * - warn: Allowed but notable (already_applied, retry)
 * - error: Errors (always shown, sent to Sentry in prod)
 * - debug: Dev-only verbose logging (never in production)
 * 
 * Usage:
 *   import { log } from '@/lib/logger';
 *   log.info('[purchase] started', { itemId });
 *   log.warn('[session] retrying');
 *   log.error('[purchase] failed', { reason });
 *   log.debug('[rpc] payload', payload);
 * 
 * Security:
 * - Never log full tokens; use log.safeToken() for partial display
 * - Debug logs are stripped in production
 */

const isDev = typeof window !== 'undefined' 
  ? !window.location.hostname.includes('lovable.app') && !window.location.hostname.includes('lovableproject.com')
  : process.env.NODE_ENV !== 'production';

// Check URL param for verbose debug mode
const getDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('debug') === '1';
  } catch {
    return false;
  }
};

const DEBUG_ENABLED = getDebugEnabled();

/**
 * Safely truncate a token for logging (last 6 chars only)
 */
const safeToken = (token: string | undefined | null): string => {
  if (!token) return '[no-token]';
  if (token.length <= 6) return '[short]';
  return `...${token.slice(-6)}`;
};

/**
 * Safely truncate a UUID for logging (last 8 chars only)
 */
const safeId = (id: string | undefined | null): string => {
  if (!id) return '[no-id]';
  if (id.length <= 8) return id;
  return `...${id.slice(-8)}`;
};

/**
 * Format meta data for console output
 */
const formatMeta = (meta?: unknown): unknown => {
  if (meta === undefined || meta === null) return '';
  return meta;
};

export const log = {
  /**
   * INFO: Expected events that are good to track
   * Always shown in dev, shown in prod
   */
  info: (msg: string, meta?: unknown): void => {
    console.info(msg, formatMeta(meta));
  },

  /**
   * WARN: Allowed but notable events
   * Always shown
   */
  warn: (msg: string, meta?: unknown): void => {
    console.warn(msg, formatMeta(meta));
  },

  /**
   * ERROR: Errors - always shown
   */
  error: (msg: string, meta?: unknown): void => {
    console.error(msg, formatMeta(meta));
  },

  /**
   * DEBUG: Verbose logging for development only
   * Only shown when ?debug=1 in URL or in dev environment
   */
  debug: (msg: string, meta?: unknown): void => {
    if (isDev || DEBUG_ENABLED) {
      console.debug(msg, formatMeta(meta));
    }
  },

  /**
   * Utility: Safely truncate token for logging
   */
  safeToken,

  /**
   * Utility: Safely truncate UUID for logging
   */
  safeId,
};

export default log;
